import React from "react";
import { Upload, Button, Alert, Flex, Space, Typography } from "antd";
import type { UploadFile } from "antd/es/upload/interface";
import {
  CheckCircleFilled,
  DeleteOutlined,
  EyeOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import { downloadExcludeTemplate } from "../../utils/excludeMember";

const { Text } = Typography;

interface ExcludeMemberUploadProps {
  fileList: UploadFile[];
  memberCount: number;
  parsing: boolean;
  beforeUpload: (file: File) => boolean | Promise<void> | any;
  onRemove: () => void;
  onPreview: () => void;
}

/**
 * UI for the "Exclude Targeted Members" upload field used in Create/Update
 * campaign. Purely presentational — all parsing/state/backend handling stays
 * in the parent and is passed in via props.
 */
const ExcludeMemberUpload: React.FC<ExcludeMemberUploadProps> = ({
  fileList,
  memberCount,
  parsing,
  beforeUpload,
  onRemove,
  onPreview,
}) => {
  const hasFile = fileList.length > 0;
  const fileName = fileList[0]?.name ?? "Exclusion list";

  const uploadTrigger = (label: string) => (
    <Upload
      accept=".xlsx,.xls"
      maxCount={1}
      fileList={fileList}
      beforeUpload={beforeUpload}
      onRemove={onRemove}
      showUploadList={false}
    >
      <Button
        type="primary"
        icon={label === "Choose File" ? <UploadOutlined /> : undefined}
        loading={parsing}
        disabled={parsing}
      >
        {label}
      </Button>
    </Upload>
  );

  if (!hasFile) {
    return (
      <Space direction="vertical" size={8} style={{ width: "100%" }}>
        <Flex align="center" gap={12}>
          {uploadTrigger("Choose File")}
          <Text type="secondary">No file chosen</Text>
        </Flex>
        <Text type="secondary">
          Upload an exclusion list for this campaign. XLSX format only.
        </Text>
        <Alert
          type="info"
          showIcon
          message={
            <span>
              Use the provided template to ensure the correct file format.{" "}
              <Button
                type="link"
                style={{ padding: 0, height: "auto" }}
                onClick={downloadExcludeTemplate}
              >
                Download Template
              </Button>
            </span>
          }
        />
      </Space>
    );
  }

  return (
    <Space direction="vertical" size={12} style={{ width: "100%" }}>
      <Flex
        align="center"
        justify="space-between"
        gap={12}
        style={{ border: "1px solid #E2E8F0", borderRadius: 8, padding: 12 }}
      >
        <Flex align="center" gap={10} style={{ minWidth: 0 }}>
          <CheckCircleFilled style={{ color: "#12B76A", fontSize: 22 }} />
          <div style={{ minWidth: 0 }}>
            <Text type="secondary" style={{ display: "block", fontSize: 12 }}>
              File Selected
            </Text>
            <Text strong ellipsis style={{ display: "block" }}>
              {fileName}
            </Text>
          </div>
        </Flex>
        <Flex align="center" gap={8}>
          <Button
            type="text"
            icon={<DeleteOutlined style={{ color: "#DC2626" }} />}
            onClick={onRemove}
          />
          {uploadTrigger("Change")}
        </Flex>
      </Flex>

      {memberCount > 0 && (
        <Alert
          type="info"
          showIcon
          icon={
            <img
              src="/icon/icon-exclude-member.svg"
              alt="excluded members"
              style={{ width: 40, height: 40 }}
            />
          }
          message={
            <Flex align="center" justify="space-between" gap={12}>
              <div>
                <div style={{ fontSize: 16, fontWeight: 600 }}>
                  {memberCount} Excluded Members
                </div>
                <div style={{ fontSize: 13, color: "#595959" }}>
                  These members will be excluded from this campaign.
                </div>
              </div>
              <Button type="primary" icon={<EyeOutlined />} onClick={onPreview}>
                View Excluded
              </Button>
            </Flex>
          }
        />
      )}
    </Space>
  );
};

export default ExcludeMemberUpload;
